/* ============================================================
   Static centroid lookup for the geospatial scan rail. The API
   does not ship lat/lon per signal, so we resolve a coordinate
   from the signal's location text (US state for NWSS / CONUS,
   country or US state for GDELT / world) against a bundled
   centroid table. Signals we can't place are dropped from the map
   (and reported in the HUD count), never plotted at 0,0.
   ============================================================ */
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import { getLocationLabel } from "./biothreatV2Signal"
import { COUNTRY_CENTROIDS } from "./biothreatV2CountryCentroids"
import { US_STATE_CENTROIDS } from "./biothreatV2UsStateCentroids"

export interface LatLon {
  lat: number
  lon: number
}

const US_STATE_ABBR: Record<string, string> = {
  al: "alabama",
  ak: "alaska",
  az: "arizona",
  ar: "arkansas",
  ca: "california",
  co: "colorado",
  ct: "connecticut",
  de: "delaware",
  dc: "district of columbia",
  fl: "florida",
  ga: "georgia",
  hi: "hawaii",
  id: "idaho",
  il: "illinois",
  in: "indiana",
  ia: "iowa",
  ks: "kansas",
  ky: "kentucky",
  la: "louisiana",
  me: "maine",
  md: "maryland",
  ma: "massachusetts",
  mi: "michigan",
  mn: "minnesota",
  ms: "mississippi",
  mo: "missouri",
  mt: "montana",
  ne: "nebraska",
  nv: "nevada",
  nh: "new hampshire",
  nj: "new jersey",
  nm: "new mexico",
  ny: "new york",
  nc: "north carolina",
  nd: "north dakota",
  oh: "ohio",
  ok: "oklahoma",
  or: "oregon",
  pa: "pennsylvania",
  ri: "rhode island",
  sc: "south carolina",
  sd: "south dakota",
  tn: "tennessee",
  tx: "texas",
  ut: "utah",
  vt: "vermont",
  va: "virginia",
  wa: "washington",
  wv: "west virginia",
  wi: "wisconsin",
  wy: "wyoming",
}

/**
 * GDELT/OSINT location names that differ from the Natural Earth names used by
 * `COUNTRY_CENTROIDS`. Maps a normalized GDELT name → the centroid table key.
 * Most countries (Uganda, Kenya, Nigeria, …) match directly and need no entry.
 */
const COUNTRY_ALIASES: Record<string, string> = {
  "united states": "united states of america",
  usa: "united states of america",
  us: "united states of america",
  "u.s.": "united states of america",
  "u.s.a.": "united states of america",
  america: "united states of america",
  uk: "united kingdom",
  "u.k.": "united kingdom",
  britain: "united kingdom",
  "great britain": "united kingdom",
  england: "united kingdom",
  "democratic republic of the congo": "dem. rep. congo",
  "dr congo": "dem. rep. congo",
  drc: "dem. rep. congo",
  "congo-kinshasa": "dem. rep. congo",
  "republic of the congo": "congo",
  "congo-brazzaville": "congo",
  "ivory coast": "côte d'ivoire",
  "cote d'ivoire": "côte d'ivoire",
  "south sudan": "s. sudan",
  "czech republic": "czechia",
  burma: "myanmar",
  "north macedonia": "macedonia",
  "east timor": "timor-leste",
  swaziland: "eswatini",
  "cape verde": "cabo verde",
}

const normalize = (value: string): string => value.trim().toLowerCase()

/** Resolve a country by Natural Earth name or a known GDELT name variant. */
const resolveCountry = (value: string): LatLon | null => {
  const key = normalize(value)
  const direct = COUNTRY_CENTROIDS[key]
  if (direct) return direct
  const aliasKey = COUNTRY_ALIASES[key]
  return aliasKey ? (COUNTRY_CENTROIDS[aliasKey] ?? null) : null
}

/** Resolve a US state by full name or 2-letter abbreviation. */
const resolveUsState = (value: string): LatLon | null => {
  const key = normalize(value)
  const direct = US_STATE_CENTROIDS[key]
  if (direct) return direct
  const fromAbbr = US_STATE_ABBR[key]
  return fromAbbr ? (US_STATE_CENTROIDS[fromAbbr] ?? null) : null
}

/** Resolve a coordinate for a signal given the active map scope. */
export const resolveSignalLatLon = (
  row: BiothreatSignal,
  view: "gdelt" | "nwss"
): LatLon | null => {
  if (view === "nwss") {
    const state = row.nwss_meta?.state_territory ?? row.resolved_location
    return state ? resolveUsState(state) : null
  }
  // GDELT: the location may be a country or a US state.
  const label = getLocationLabel(row)
  return resolveCountry(label) ?? resolveUsState(label)
}
